import BatchLabelProgressDrawer from '@/pages/Business/pages/Shippings/components/BatchLabelProgressDrawer';
import RowActionsMenu, { type RowAction } from '@/components/table/RowActionsMenu';
import AssignDriverModal from './AssignDriverModal';
import AddShippingsModal from './AddShippingsModal';
import CancelRouteModal from './CancelRouteModal';
import { useRouteCardActions } from '../hooks';
import { IconButton } from '@mui/material';
import { Route } from '../models/Route';
import {
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  PostAdd as PostAddIcon,
  CancelOutlined as CancelOutlinedIcon,
  ContentCopy as ContentCopyIcon,
  Print as PrintIcon,
} from '@mui/icons-material';

const ACTION_ICON_ORANGE_SX = {
  fontSize: 28,
  color: 'var(--primary-color)',
} as const;

const ACTION_ICON_DANGER_SX = {
  fontSize: 22,
  color: '#C62828',
} as const;

const MENU_ICON_SX = { fontSize: 22 } as const;

interface RouteRowActionsProps {
  route: Route;
}

const RouteRowActions = ({ route }: RouteRowActionsProps) => {
  const {
    assignDriverModalOpen,
    closeAssignDriverModal,
    addShippingsModalOpen,
    closeAddShippingsModal,
    cancelRouteModalOpen,
    closeCancelRouteModal,
    hasDriver,
    isCancelled,
    isReadOnly,
    canAddShippings,
    canEditRoute,
    canAssignDriver,
    canCancelRoute,
    canCloneRoute,
    allowCancelRoute,
    driverActionLabel,
    driverActionDisabledReason,
    isCancelling,
    isCloning,
    handleEditRoute,
    handleAssignDriverClick,
    handleAddShippingsClick,
    handleCancelRouteClick,
    handleConfirmCancelRoute,
    handleCloneRouteClick,
    canPrintLabels,
    printLabelsDisabledReason,
    isPrintingLabels,
    labelsJob,
    dismissLabelsJob,
    handlePrintLabelsClick,
  } = useRouteCardActions({ route });

  const actions: RowAction[] = [
    {
      key: 'assign-driver',
      label: driverActionLabel,
      icon: <PersonAddIcon sx={MENU_ICON_SX} color='primary' />,
      onClick: handleAssignDriverClick,
      disabled: !canAssignDriver,
      reason: driverActionDisabledReason,
      hidden: isReadOnly,
    },
    {
      key: 'print-labels',
      label: 'Imprimir etiquetas',
      icon: <PrintIcon sx={MENU_ICON_SX} color='primary' />,
      onClick: handlePrintLabelsClick,
      disabled: !canPrintLabels || isPrintingLabels,
      reason: isPrintingLabels
        ? 'Ya se están generando las etiquetas de esta ruta.'
        : printLabelsDisabledReason,
      hidden: isCancelled,
    },
    {
      key: 'cancel-route',
      label: 'Cancelar ruta',
      icon: <CancelOutlinedIcon sx={ACTION_ICON_DANGER_SX} />,
      onClick: handleCancelRouteClick,
      disabled: !canCancelRoute,
      reason: 'Solo se pueden cancelar rutas que todavía no salieron a recorrer.',
      hidden: !allowCancelRoute || isReadOnly,
    },
    {
      key: 'clone-route',
      label: 'Clonar ruta',
      icon: <ContentCopyIcon sx={MENU_ICON_SX} color='primary' />,
      onClick: handleCloneRouteClick,
      disabled: isCloning || !canCloneRoute,
      reason: 'Solo se pueden clonar rutas canceladas.',
      hidden: !isCancelled,
    },
  ];

  return (
    <>
      <div className='flex flex-row items-center justify-end gap-1'>
        {!isReadOnly && (
          <>
            <IconButton
              onClick={handleAddShippingsClick}
              title={
                canAddShippings
                  ? 'Agregar pedidos'
                  : 'La ruta no tiene operador logístico.'
              }
              disabled={!canAddShippings}
              size='medium'
            >
              <PostAddIcon sx={ACTION_ICON_ORANGE_SX} />
            </IconButton>
            <IconButton
              onClick={handleEditRoute}
              title={
                canEditRoute
                  ? 'Editar ruta'
                  : 'La ruta no tiene operador logístico.'
              }
              disabled={!canEditRoute}
              size='small'
            >
              <EditIcon />
            </IconButton>
          </>
        )}
        <RowActionsMenu actions={actions} />
      </div>

      <BatchLabelProgressDrawer job={labelsJob} onClose={dismissLabelsJob} />
      {canAssignDriver && (
        <AssignDriverModal
          open={assignDriverModalOpen}
          onClose={closeAssignDriverModal}
          routeId={route.id}
          logisticOperatorId={route.logisticOperatorId}
          isReassignment={hasDriver}
          onSuccess={closeAssignDriverModal}
        />
      )}
      {canAddShippings && (
        <AddShippingsModal
          open={addShippingsModalOpen}
          onClose={closeAddShippingsModal}
          routeId={route.id}
          logisticOperatorId={route.logisticOperatorId ?? ''}
          onSuccess={closeAddShippingsModal}
        />
      )}
      {canCancelRoute && (
        <CancelRouteModal
          open={cancelRouteModalOpen}
          onClose={closeCancelRouteModal}
          onConfirm={handleConfirmCancelRoute}
          isSubmitting={isCancelling}
          routeName={route.name}
          ordersCount={route.ordersCount}
        />
      )}
    </>
  );
};

export default RouteRowActions;
